import * as THREE from 'three';

/**
 * Engine: dono do laco de frame, das cenas e das cameras.
 *
 * Duas cenas por design (padrao de FPS):
 *   scene      + camera      -> o mundo (FOV do jogador, sujeito a pos-processamento)
 *   viewScene  + viewCamera  -> a arma em primeira pessoa, renderizada DEPOIS com
 *                               o depth buffer limpo, para a arma nunca atravessar parede.
 *                               viewCamera acompanha camera, mas usa FOV menor e fixo.
 *
 * Sistemas registrados com engine.add(sys) podem implementar, todos opcionais:
 *   fixedUpdate(h)  -> passo fixo de 1/120s. Movimento, colisao e IA vao AQUI.
 *   update(dt)      -> passo variavel. Efeitos visuais, camera, animacao, UI.
 *   lateUpdate(dt)  -> depois de todo update. Camera final, HUD.
 *   dispose()
 *
 * A ORDEM de registro e a ordem de execucao. Ela e definida em main.js.
 */

const FIXED_STEP = 1 / 120;
const MAX_STEPS_PER_FRAME = 8;
const MAX_FRAME_DT = 0.1;

export class Engine {
  constructor({ canvas, settings, events }) {
    this.canvas = canvas;
    this.settings = settings;
    this.events = events;

    this.scene = new THREE.Scene();
    this.viewScene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(settings.fov, 1, 0.05, 1200);
    this.camera.rotation.order = 'YXZ';
    this.viewCamera = new THREE.PerspectiveCamera(55, 1, 0.002, 12);
    this.viewCamera.rotation.order = 'YXZ';

    /** Preenchido pelo RendererSystem. @type {THREE.WebGLRenderer|null} */
    this.renderer = null;
    /** Preenchido pelo PostFX. Se null, o Engine faz render direto. */
    this.composer = null;

    this.systems = [];
    this.running = false;
    this.paused = false;
    this.elapsed = 0;
    this.frame = 0;
    this.dt = 0;
    this.fps = 0;

    this._accumulator = 0;
    this._last = 0;
    this._fpsAccum = 0;
    this._fpsFrames = 0;
    this._rafId = 0;
    this._tick = this._tick.bind(this);

    this._resizeObserver = null;
    this._onWindowResize = () => this.resize();
    window.addEventListener('resize', this._onWindowResize);
    document.addEventListener('visibilitychange', () => {
      // Volta de aba oculta sem um dt gigante que teleporta o jogador.
      if (!document.hidden) this._last = performance.now();
    });
  }

  add(system) {
    if (system) this.systems.push(system);
    return system;
  }

  /** Tamanho de renderizacao em pixels, ja com resolutionScale e devicePixelRatio. */
  getRenderSize() {
    const scale = this.settings.resolutionScale ?? 1;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    return {
      width: Math.max(1, Math.floor(window.innerWidth * dpr * scale)),
      height: Math.max(1, Math.floor(window.innerHeight * dpr * scale)),
      cssWidth: window.innerWidth,
      cssHeight: window.innerHeight,
      pixelRatio: dpr * scale
    };
  }

  resize() {
    const { cssWidth, cssHeight, pixelRatio } = this.getRenderSize();
    const aspect = cssWidth / Math.max(1, cssHeight);

    this.camera.aspect = aspect;
    this.camera.fov = this.settings.fov;
    this.camera.updateProjectionMatrix();

    this.viewCamera.aspect = aspect;
    this.viewCamera.updateProjectionMatrix();

    if (this.renderer) {
      this.renderer.setPixelRatio(pixelRatio);
      this.renderer.setSize(cssWidth, cssHeight, false);
    }
    if (this.composer?.setSize) this.composer.setSize(cssWidth, cssHeight);

    for (const sys of this.systems) sys.onResize?.(cssWidth, cssHeight);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this._last = performance.now();
    this._rafId = requestAnimationFrame(this._tick);
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this._rafId);
  }

  _tick(now) {
    if (!this.running) return;
    this._rafId = requestAnimationFrame(this._tick);

    const raw = (now - this._last) / 1000;
    this._last = now;
    const dt = Math.min(raw, MAX_FRAME_DT);
    this.dt = dt;
    this.elapsed += dt;
    this.frame++;

    this._fpsAccum += raw;
    this._fpsFrames++;
    if (this._fpsAccum >= 0.5) {
      this.fps = this._fpsFrames / this._fpsAccum;
      this._fpsAccum = 0;
      this._fpsFrames = 0;
    }

    if (!this.paused) {
      this._accumulator += dt;
      let steps = 0;
      while (this._accumulator >= FIXED_STEP && steps < MAX_STEPS_PER_FRAME) {
        for (const sys of this.systems) sys.fixedUpdate?.(FIXED_STEP);
        this._accumulator -= FIXED_STEP;
        steps++;
      }
      if (steps === MAX_STEPS_PER_FRAME) this._accumulator = 0; // evita espiral da morte

      for (const sys of this.systems) sys.update?.(dt, this.elapsed);
      for (const sys of this.systems) sys.lateUpdate?.(dt, this.elapsed);
    } else {
      // Pausado: a UI continua viva, o mundo nao.
      for (const sys of this.systems) {
        if (sys.updateWhilePaused) sys.update?.(dt, this.elapsed);
      }
    }

    this.viewCamera.position.copy(this.camera.position);
    this.viewCamera.quaternion.copy(this.camera.quaternion);

    this.render(dt);
  }

  render(dt) {
    if (this.composer) {
      this.composer.render(dt);
    } else if (this.renderer) {
      this.renderer.render(this.scene, this.camera);
      this.renderer.autoClear = false;
      this.renderer.clearDepth();
      this.renderer.render(this.viewScene, this.viewCamera);
      this.renderer.autoClear = true;
    }
  }

  dispose() {
    this.stop();
    window.removeEventListener('resize', this._onWindowResize);
    for (const sys of this.systems) sys.dispose?.();
    this.systems.length = 0;
  }
}
